(function () {
  'use strict';

  // ─── Single debounced resize dispatcher ─────────────────────────────────────
  // All resize work registers here instead of attaching separate listeners.
  // Replaces 4 scattered window.addEventListener('resize', ...) calls.
  const resizeCallbacks = [];
  let globalResizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(globalResizeTimer);
    globalResizeTimer = setTimeout(function () {
      resizeCallbacks.forEach(function (fn) { fn(); });
    }, 150);
  }, { passive: true });

  // Cached sticky-header height — avoids a getBoundingClientRect() reflow on
  // every nav-link click; invalidated by the resize dispatcher above.
  let cachedScrollOffset = null;

  /** Theme sticky header (#shopify-section-header). Sync + scroll-margin-top so anchors land below it. */
  function updateRingGuideScrollOffset() {
    const header = document.getElementById('shopify-section-header');
    const buffer = 32;
    const fallback = 200;
    let h = fallback;
    if (header) {
      const rect = header.getBoundingClientRect();
      h = Math.ceil(rect.height) + buffer;
      if (h < 120) {
        h = fallback;
      }
    }
    cachedScrollOffset = h;
    document.documentElement.style.setProperty('--emrise-rg-scroll-offset', h + 'px');
  }

  function bindRingGuideScrollOffsetUpdates() {
    updateRingGuideScrollOffset();
    // Invalidate cache and recompute on resize via the global dispatcher.
    resizeCallbacks.push(function () {
      cachedScrollOffset = null;
      updateRingGuideScrollOffset();
    });
    window.addEventListener('load', updateRingGuideScrollOffset, { passive: true });
  }

  function scrollRingGuideHeadingIntoView(el, behavior) {
    if (!el || !el.scrollIntoView) return;
    // Use cached offset — skip the reflow-triggering getBoundingClientRect on every click.
    if (cachedScrollOffset === null) updateRingGuideScrollOffset();
    window.requestAnimationFrame(function () {
      el.scrollIntoView({ behavior: behavior || 'smooth', block: 'start' });
    });
  }

  function scrollRingGuideHashIntoView(behavior) {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    let id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return;
    const target = document.getElementById(id);
    if (!target || !target.hasAttribute('data-emrise-nav-heading')) return;
    scrollRingGuideHeadingIntoView(target, behavior || 'auto');
  }

  function isDesktop() {
    return window.innerWidth >= 990;
  }

  function slugify(text) {
    return text
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  function initLayout() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    const contentCol = document.querySelector('[data-emrise-ring-guide-content]');
    if (!contentCol || contentCol.dataset.emriseLayoutReady === 'true') return;

    const guideSections = main.querySelectorAll('.shopify-section.emrise-ring-guide-section');
    if (!guideSections.length) return;

    // Batch all appends through a DocumentFragment — single reflow instead of one per section.
    const fragment = document.createDocumentFragment();
    guideSections.forEach(function (section) {
      fragment.appendChild(section);
    });
    contentCol.appendChild(fragment);

    contentCol.dataset.emriseLayoutReady = 'true';
  }

  function initSidebarNav() {
    const content = document.querySelector('[data-emrise-ring-guide-content]');
    const navList = document.querySelector('[data-emrise-nav-list]');
    if (!content || !navList) return;

    navList.innerHTML = '';

    const headingSelector =
      'h1[data-emrise-nav-heading], h2[data-emrise-nav-heading], h3[data-emrise-nav-heading], h4[data-emrise-nav-heading], h5[data-emrise-nav-heading], h6[data-emrise-nav-heading]';

    const guideSections = content.querySelectorAll('.shopify-section.emrise-ring-guide-section');
    const headings = [];

    guideSections.forEach(function (sectionEl) {
      const primaryHeading = sectionEl.querySelector(headingSelector);
      if (primaryHeading) {
        headings.push(primaryHeading);
      }
    });

    const usedIds = {};
    const navItems = [];

    headings.forEach(function (heading) {
      const text = heading.textContent.trim();
      if (!text) return;

      let id = heading.id;
      if (!id) {
        const base = slugify(text);
        id = base;
        let counter = 1;
        while (usedIds[id]) {
          id = base + '-' + counter;
          counter++;
        }
        heading.id = id;
      }
      usedIds[id] = true;

      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + id;
      a.textContent = text;
      a.setAttribute('data-emrise-nav-link', '');
      li.appendChild(a);
      navList.appendChild(li);
      navItems.push({ link: a, heading: heading });
    });

    if (!navItems.length) return;

    function setActive(activeLink) {
      navItems.forEach(function (item) {
        item.link.classList.toggle('is-active', item.link === activeLink);
      });
    }

    navItems.forEach(function (item) {
      item.link.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.getElementById(item.heading.id);
        if (target) {
          scrollRingGuideHeadingIntoView(target, 'smooth');
          setActive(item.link);
          history.replaceState(null, '', '#' + item.heading.id);
        }
      });
    });

    if ('IntersectionObserver' in window) {
      // Map<heading element → nav link> for O(1) lookup — replaces Array.find() O(n) per entry.
      const headingMap = new Map(navItems.map(function (item) {
        return [item.heading, item.link];
      }));

      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          const link = headingMap.get(entry.target);
          if (link) setActive(link);
        });
      }, { rootMargin: '-20% 0px -60% 0px', threshold: 0 });

      navItems.forEach(function (item) {
        observer.observe(item.heading);
      });
    } else if (navItems[0]) {
      setActive(navItems[0].link);
    }
  }

  function initContentImageHeight() {
    if (window.innerWidth < 990) return;

    document.querySelectorAll('[data-emrise-content-grid]').forEach(function (grid) {
      const blocks = grid.querySelector('.emrise-content__blocks');
      const mediaInner = grid.querySelector('.emrise-content__media-inner');
      if (!blocks || !mediaInner) return;

      const setHeight = function () {
        mediaInner.style.maxHeight = blocks.offsetHeight + 'px';
      };

      setHeight();
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(setHeight);
        ro.observe(blocks);
      }
    });
  }

  function initRecentBlogs() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('[data-emrise-recent-blogs]').forEach(function (root) {
      if (root.dataset.emriseBlogsReady === 'true') return;

      const swiperEl = root.querySelector('.emrise-recent-blogs__swiper');
      if (!swiperEl) return;

      const isStaticDesktop = root.classList.contains('emrise-recent-blogs--static-desktop');
      const prevEl = root.querySelector('.emrise-recent-blogs__arrow--prev');
      const nextEl = root.querySelector('.emrise-recent-blogs__arrow--next');
      const paginationEl = root.querySelector('.emrise-recent-blogs__pagination');
      let swiperInstance = null;

      function mountSwiper() {
        if (isStaticDesktop && isDesktop()) {
          if (swiperInstance) { swiperInstance.destroy(true, true); swiperInstance = null; }
          return;
        }

        if (swiperInstance) {
          swiperInstance.update();
          return;
        }

        swiperInstance = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 20,
          watchOverflow: true,
          pagination: {
            el: paginationEl,
            clickable: true
          },
          navigation: {
            prevEl: prevEl,
            nextEl: nextEl
          },
          breakpoints: {
            990: {
              slidesPerView: 3,
              spaceBetween: 30,
              pagination: false
            }
          }
        });
      }

      mountSwiper();

      // Only tear down/remount when the desktop↔mobile breakpoint actually
      // crosses — avoids Swiper destroy+create on every resize tick.
      let wasDesktop = isDesktop();
      resizeCallbacks.push(function () {
        const nowDesktop = isDesktop();
        if (nowDesktop !== wasDesktop) {
          wasDesktop = nowDesktop;
          if (swiperInstance) { swiperInstance.destroy(true, true); swiperInstance = null; }
          mountSwiper();
        } else if (swiperInstance) {
          swiperInstance.update();
        }
      });

      root.dataset.emriseBlogsReady = 'true';
    });
  }

  function updateRingCollectionsMiddleSlide(swiper) {
    if (!swiper || swiper.destroyed) return;
    swiper.slides.forEach(function (s) {
      s.classList.remove('emrise-ring-collections__slide--middle');
    });
    if (window.innerWidth < 990) return;
    const visible = Array.prototype.filter.call(swiper.slides, function (s) {
      return s.classList.contains('swiper-slide-visible');
    });
    if (visible.length >= 3) {
      visible[1].classList.add('emrise-ring-collections__slide--middle');
    }
  }

  function initRingCollections() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('[data-emrise-ring-collections]').forEach(function (root) {
      if (root.dataset.emriseCollectionsReady === 'true') return;

      const swiperEl = root.querySelector('.emrise-ring-collections__swiper');
      if (!swiperEl) {
        root.dataset.emriseCollectionsReady = 'true';
        return;
      }

      const isStaticDesktop = root.classList.contains('emrise-ring-collections--static-desktop');
      const prevEl = root.querySelector('.emrise-ring-collections__arrow--prev');
      const nextEl = root.querySelector('.emrise-ring-collections__arrow--next');
      const paginationEl = root.querySelector('.emrise-ring-collections__pagination');
      let swiperInstance = null;

      function mountSwiper() {
        if (isStaticDesktop && isDesktop()) {
          if (swiperInstance) { swiperInstance.destroy(true, true); swiperInstance = null; }
          return;
        }

        if (swiperInstance) {
          swiperInstance.update();
          updateRingCollectionsMiddleSlide(swiperInstance);
          return;
        }

        swiperInstance = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 20,
          watchOverflow: true,
          pagination: {
            el: paginationEl,
            clickable: true
          },
          navigation: {
            prevEl: prevEl,
            nextEl: nextEl
          },
          breakpoints: {
            990: {
              slidesPerView: 3,
              spaceBetween: 35,
              pagination: false
            }
          },
          on: {
            init: function () {
              updateRingCollectionsMiddleSlide(this);
            },
            slideChangeTransitionEnd: function () {
              updateRingCollectionsMiddleSlide(this);
            },
            resize: function () {
              updateRingCollectionsMiddleSlide(this);
            }
          }
        });
        updateRingCollectionsMiddleSlide(swiperInstance);
      }

      mountSwiper();

      // Only tear down/remount when the desktop↔mobile breakpoint actually
      // crosses — avoids Swiper destroy+create on every resize tick.
      let wasDesktop = isDesktop();
      resizeCallbacks.push(function () {
        const nowDesktop = isDesktop();
        if (nowDesktop !== wasDesktop) {
          wasDesktop = nowDesktop;
          if (swiperInstance) { swiperInstance.destroy(true, true); swiperInstance = null; }
          mountSwiper();
        } else if (swiperInstance) {
          swiperInstance.update();
          updateRingCollectionsMiddleSlide(swiperInstance);
        }
      });

      root.dataset.emriseCollectionsReady = 'true';
    });
  }

  function onReady() {
    bindRingGuideScrollOffsetUpdates();
    initLayout();
    initSidebarNav();
    scrollRingGuideHashIntoView('auto');
    window.addEventListener('hashchange', function () {
      scrollRingGuideHashIntoView('smooth');
    });
    initContentImageHeight();
    // Route initContentImageHeight through the global debounced dispatcher
    // instead of the previous unthrottled window.addEventListener('resize', ...).
    resizeCallbacks.push(initContentImageHeight);
    // Track whether Swiper was available at DOMContentLoaded — if it was, the
    // load-event retry is skipped because data-*Ready flags already guard re-init.
    const swiperAvailableNow = typeof Swiper !== 'undefined';
    initRecentBlogs();
    initRingCollections();
    // Retry hash scroll once all resources are ready (images, fonts).
    // Only re-run Swiper inits if Swiper wasn't available above (e.g. loaded with defer).
    window.addEventListener('load', function () {
      scrollRingGuideHashIntoView('auto');
      if (!swiperAvailableNow) {
        initRecentBlogs();
        initRingCollections();
      }
    }, { once: true, passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
